import * as tabNavigation from "@grindex-client/components/TabNavigation/TabNavigation";
import { addListener, width } from "@grindex-common/helpers/domManipulation";
import { Expandable } from "@grindex-components/Accordion/Expandable.class";
import { constants } from "@grindex-components/Accordion/Accordion.constants";
import { Route } from "@grindex-client/components/Route/route.js";
import { Resolution } from "@grindex-common/helpers/Resolution.class";
import { events } from "@grindex-common/helpers/events";
import { classes, elements } from "@grindex-common/helpers/cssGlobals";
import TabsScrollUi from "@grindex-client/components/Tabs/Tab.Scroll.UI.class";

const CLASS_SELECTED_TAB = "tab_selected";
const CLASS_VISIBLE_DESKTOP = "visible-desktop";

class TabView {
	constructor(routerRoot, routes, $tabView, ignoreRouting) {
		this.$tabView = $tabView;
		this.ignoreRouting = ignoreRouting;
		this.resolution = new Resolution();
		this.$accordionItem = $tabView.querySelector("[data-accordion-breakpoint]");
		this.targetResolution = this.$accordionItem ? this.$accordionItem.dataset.accordionBreakpoint : "desktopLarge";
		this.$scrollableTabsContainer = document.querySelector(elements.TAB_HEADERS);
		this.scrollTabs = new TabsScrollUi(this.$scrollableTabsContainer);

		this.router = new Route({
			root: routerRoot,
			router: routes,
			tabChanged: this.tabChanged.bind(this),
		});
	}

	init() {
		this.$mobileTabHeaders = this.$tabView.getElementsByClassName("mobile-tab-header");

		this.addListeners();

		this.$mobileTabHeaders.forEach(($tab) => {
			const expandable = new Expandable($tab);
			addListener($tab, "click", expandable.toggle);
		});

		this.setupTabs();
		this.scrollTabs.initializeScrollableTabs(this.onTabClick.bind(this));
	}

	addListeners() {
		addListener(window, events.TABS_SWITCHED, this.scrollTableWidths.bind(this));
		addListener(window, "applyToggleFilters", this.scrollTableWidths.bind(this));
		addListener(window, "resize", this.scrollTableWidths.bind(this));
		addListener(window, "load", this.scrollTableWidths.bind(this));
	}

	setupTabs() {
		if (!this.async) {
			tabNavigation.init();
		}

		this.addListeners();

		this.startRouter();
	}

	switchToTab(tabIndex, skipNavigation) {
		const $selectedTab = this.getTabByIndex(tabIndex);

		this.$mobileTabHeaders.forEach(($mobileHeader) => {
			$mobileHeader.classList.remove(classes.SELECTED);
		});

		this.showTab($selectedTab, skipNavigation);

		// If any card blocks, re-render them with this tab
		// visible so the correct height info can be culled from the DOM
		window.addEventListener("DOMContentLoaded", () => {
			window.dispatchEvent(new CustomEvent(events.TABS_SWITCHED));
		});

		if (this.$sideBar) {
			if (this.tabList.currentTabIs(this.productTabNames.overview)) {
				this.$sideBar.parentNode.classList.remove("hidden");
				this.$sideBar.classList.remove("hidden");
			} else {
				this.$sideBar.parentNode.classList.add("hidden");
				this.$sideBar.classList.add("hidden");
			}
		}

		$selectedTab.classList.add(classes.SELECTED);
	}

	showTab($tab, skipNavigation) {
		const $otherTabs = this.getTabsExcept($tab.dataset.tab);
		const $tabHeaders = this.$tabView.querySelectorAll("span[data-tab]");

		$tabHeaders.forEach(($tabHeader) => {
			if ($tabHeader.dataset.tab === $tab.dataset.tab) {
				$tabHeader.classList.add(classes.SELECTED);
				$tabHeader.classList.add(CLASS_SELECTED_TAB);
			} else {
				$tabHeader.classList.remove(classes.SELECTED);
				$tabHeader.classList.remove(CLASS_SELECTED_TAB);
			}
		});

		$tab.classList.add(CLASS_VISIBLE_DESKTOP);
		$tab.parentElement.classList.add(constants.CLASS_ACTIVE);

		const $tabElement = this.getTabByIndex($tab.dataset.tab);

		if ($tabElement) {
			$tabElement.classList.add(CLASS_VISIBLE_DESKTOP);
		}

		for (let i = 0; i < $otherTabs.length; i += 1) {
			this.hideTab($otherTabs[i]);
		}

		if (!skipNavigation && !this.ignoreRouting) {
			this.router.navigate($tab.dataset.tabName);
		}
	}

	hideTab($tab) {
		$tab.classList.remove(CLASS_VISIBLE_DESKTOP);
		$tab.parentElement.classList.remove(constants.CLASS_ACTIVE);

		const $tabElement = this.getTabByIndex($tab.dataset.tab);

		if ($tabElement) {
			$tabElement.classList.remove(CLASS_VISIBLE_DESKTOP);
		}
	}

	scrollToMenu($header) {
		window.scrollTo($header.offsetTop, 0);
	}

	onTabClick(e) {
		const tabIndex = e.currentTarget.dataset.tab;
		this.switchToTab(tabIndex);
		e.preventDefault();
	}

	scrollTableWidths() {
		const $tableScroll = document.querySelectorAll(".technical-specifications-container");

		$tableScroll.forEach(($scrollable) => {
			const $headingRow = $scrollable.querySelector(".flex-container-heading");
			if ($headingRow) {
				const $headingCells = Array.prototype.filter.call($headingRow.childNodes, ($row) => $row.classList && !$row.classList.contains("hidden"));
				const $tableCells = this.$tabView.querySelectorAll(".flex-container > *");

				if ($headingRow) {
					this.calculateTableWidths($headingCells, $scrollable, $tableCells);
				}
			}
		});
	}

	calculateTableWidths($headingCells, $scrollable, $cells) {
		var props = Array.prototype.reduce.call(
			$headingCells,
			(accumulator, $cell) => {
				if (width($cell) > 0) {
					accumulator.cells = accumulator.cells + 1;
					accumulator.total = accumulator.total + 159;
				}

				return accumulator;
			},
			{
				total: 0,
				cells: 0,
				width: width($scrollable),
			},
		);

		$cells.forEach(($cell) => {
			$cell.style.width = props.total < props.width ? props.width / props.cells + "px" : props.total / props.cells + "px";
		});
	}

	startRouter(currentSegment) {
		const segment = currentSegment ? currentSegment : this.router.getCurrentSegment();
		const tab = this.getTabByNameOrFirstExisting(segment);

		if (!tab) {
			return;
		}
		this.switchToTab(tab.dataset.tab, true);

		if (tab.dataset.tab !== "1" && this.hideSideBar) {
			this.hideSideBar();
		}
	}

	initializeRoute(segment) {
		this.router.navigate(segment);
	}

	tabChanged(currentTabName) {
		this.tabList.setTabWithFallback(currentTabName);

		this.showTab(true);
	}

	getTabIndexOrFirstExisting(preferredTab) {
		if (this.tabExists(preferredTab)) {
			return this.getTabByIndex(preferredTab).dataset.tab;
		}

		const tab = this.$tabView.querySelector("[data-tab-name]");

		return tab ? tab.dataset.tab : -1;
	}

	getTabByNameOrFirstExisting(preferredTab) {
		var tab = this.getTabByName(preferredTab);

		if (tab) {
			return tab;
		}

		tab = this.$tabView.querySelector("[data-tab-name]");

		return tab ? tab : null;
	}

	tabExists(index) {
		return !!this.getTabByIndex(index);
	}

	getTabsExcept(tabIndex) {
		return this.$tabView.querySelectorAll(`.tab-content:not([data-tab="${tabIndex}"]`);
	}

	getTabByIndex(tabIndex) {
		return this.$tabView.querySelector(`.tab-content[data-tab="${tabIndex}"]`);
	}

	getTabByName(name) {
		return this.$tabView.querySelector(`.tab-content[data-tab-name="${name}"]`);
	}
}

export { TabView };
